/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography } from '@coursera/cds-core';
import { Link } from '@coursera/event-pulse/react';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

export type SyllabusItemTitleElement = React.ReactElement<Props, typeof SyllabusItemTitle>;

type Props = {
  title: string;
  link?: string;
  linkA11yLabel?: string;
  headingLevel?: 'h2';
  disableLink?: boolean;
};

const styles = {
  link: css`
    color: var(--cds-color-neutral-primary);

    /* doubling the current class to override CDS's blue color applied to visited links */
    &&:visited {
      color: var(--cds-color-neutral-primary);
    }

    &&:hover {
      color: var(--cds-color-interactive-primary);
      text-decoration: underline;
    }
  `,
  title: css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
};

const SyllabusItemTitle = ({ title, link, linkA11yLabel, disableLink, headingLevel }: Props) => {
  const isMobile = useIsMobile();
  return (
    <React.Fragment>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold" component={headingLevel ?? 'h3'} css={!isMobile && styles.title}>
        {link && !disableLink ? (
          <Link
            href={link}
            css={styles.link}
            aria-label={linkA11yLabel}
            // TrackedLink2 is used to enable routing without page load.
            component={TrackedLink2}
            noViewTracking
            trackingData={{
              button: { name: 'description_page_syllabus_link' },
              pageSection: {
                sectionName: 'syllabus',
              },
            }}
            data-e2e="sdp-course-list-link"
          >
            {title}
          </Link>
        ) : (
          title
        )}
      </Typography>
    </React.Fragment>
  );
};

export default SyllabusItemTitle;
